import { format } from "date-fns";
import type { User } from "./types";
import { isConsentValid } from "./participant-utils";

/**
 * Formats a consent date string for display
 * @param dateString - ISO date string or null/undefined
 * @returns Formatted date string (e.g., "Jan 15, 2024") or null if no date
 */
export function formatConsentDate(
  dateString: string | null | undefined,
): string | null {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return format(date, "MMM d, yyyy");
  } catch {
    return null;
  }
}

/**
 * Gets the expiration date for a consent (6 months from consent date)
 * @param consentedAt - ISO date string or null/undefined
 * @returns Date object representing expiration, or null if no consent date
 */
export function getConsentExpirationDate(
  consentedAt: string | null | undefined,
): Date | null {
  if (!consentedAt) return null;
  try {
    const date = new Date(consentedAt);
    const expirationDate = new Date(date);
    expirationDate.setMonth(expirationDate.getMonth() + 6);
    return expirationDate;
  } catch {
    return null;
  }
}

/**
 * Gets the most recent consent date across all consent types
 * @param user - User object with consent timestamps
 * @returns ISO date string of the most recent consent, or null if no consents exist
 */
export function getMostRecentConsentDate(user: User | null | undefined): string | null {
  if (!user) return null;

  const consentDates = [
    user.dataProcessingConsentedAt,
    user.codeOfConductConsentedAt,
    user.emergencyTreatmentConsentedAt,
    user.photoConsentedAt,
    user.marketingConsentedAt,
  ].filter((date): date is string => date !== null && date !== undefined);

  if (consentDates.length === 0) return null;

  // Sort dates descending and return the most recent
  const sortedDates = consentDates
    .map((date) => new Date(date))
    .sort((a, b) => b.getTime() - a.getTime());

  return sortedDates[0].toISOString();
}

/**
 * Gets the consent status for a given consent value and timestamp
 * @param consentValue - The consent value (true/false/null/undefined)
 * @param consentedAt - ISO date string or null/undefined
 * @param isPhotoConsent - Whether this is photo consent (which is active if decision made, regardless of true/false)
 * @returns 'active' if consent is true and valid (or false with valid timestamp for photo consent), 'declined' if consent is false with valid timestamp (non-photo), 'expired' if consent is true but expired, 'missing' if no consent
 */
export function getConsentStatus(
  consentValue: boolean | null | undefined,
  consentedAt: string | null | undefined,
  isPhotoConsent: boolean = false,
): "active" | "expired" | "declined" | "missing" {
  // No timestamp means no consent decision has been made
  if (!consentedAt) return "missing";
  
  // Special handling for photo consent: active if timestamp is valid, regardless of true/false
  if (isPhotoConsent) {
    return isConsentValid(consentedAt) ? "active" : "expired";
  }
  
  // If consent is explicitly false, it's declined (even if timestamp is valid)
  if (consentValue === false) return "declined";
  
  // If consent is true, check if timestamp is still valid
  if (consentValue === true) {
    return isConsentValid(consentedAt) ? "active" : "expired";
  }
  
  // If consentValue is null/undefined but timestamp exists, treat as missing
  // (this shouldn't normally happen, but handle edge case)
  return "missing";
}

/**
 * Checks if photo consent is active for overall status purposes
 * Photo consent is considered active if a decision has been made (true or false) with valid timestamp
 * @param photoConsentedAt - ISO date string or null/undefined
 * @returns true if photo consent has a valid timestamp (regardless of true/false value)
 */
function isPhotoConsentActiveForOverall(
  photoConsentedAt: string | null | undefined,
): boolean {
  if (!photoConsentedAt) return false;
  // Photo consent is active if timestamp is valid, regardless of true/false value
  return isConsentValid(photoConsentedAt);
}

/**
 * Gets the overall consent status for a user
 * Checks required consents: data processing, code of conduct, emergency treatment, and photo consent
 * Photo consent is considered active if a decision has been made (true or false) with valid timestamp
 * @param user - User object with consent values and timestamps
 * @returns 'active' if all required consents are active, 'review-required' if any are expired/missing/declined
 */
export function getOverallConsentStatus(
  user: User | null | undefined,
): "active" | "review-required" {
  if (!user) return "review-required";

  const dataProcessingStatus = getConsentStatus(
    user.dataProcessingConsent,
    user.dataProcessingConsentedAt,
  );
  const codeOfConductStatus = getConsentStatus(
    user.consentFormCodeOfConductAccepted,
    user.codeOfConductConsentedAt,
  );
  const emergencyTreatmentStatus = getConsentStatus(
    user.emergencyTreatmentConsent,
    user.emergencyTreatmentConsentedAt,
  );
  const photoConsentActive = isPhotoConsentActiveForOverall(
    user.photoConsentedAt,
  );

  // All required consents must be active
  // Photo consent is active if timestamp is valid (regardless of true/false)
  if (
    dataProcessingStatus === "active" &&
    codeOfConductStatus === "active" &&
    emergencyTreatmentStatus === "active" &&
    photoConsentActive
  ) {
    return "active";
  }

  return "review-required";
}

